import { normalize } from 'normalizr'
import { Schemas } from 'redux/schema'
import { updateEntities, deleteEntity } from 'redux/entities'

import api from 'utils/api'

import { handleError } from '../utils'

const FETCH_REQUEST = 'contentexpress/order/FETCH_REQUEST'
const FETCH_SUCCESS = 'contentexpress/order/FETCH_SUCCESS'
const FETCH_FAILURE = 'contentexpress/order/FETCH_FAILURE'

const CREATE_REQUEST = 'contentexpress/order/CREATE_REQUEST'
const CREATE_SUCCESS = 'contentexpress/order/CREATE_SUCCESS'
const CREATE_FAILURE = 'contentexpress/order/CREATE_FAILURE'

const UPDATE_REQUEST = 'contentexpress/order/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'contentexpress/order/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'contentexpress/order/UPDATE_FAILURE'

const DELETE_REQUEST = 'contentexpress/order/DELETE_REQUEST'
const DELETE_SUCCESS = 'contentexpress/order/DELETE_SUCCESS'
const DELETE_FAILURE = 'contentexpress/order/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loading: false,
  result: {},
  updating: false,
}

// Actions
export function fetchRequest() {
  return {
    type: FETCH_REQUEST,
  }
}

export function fetchSuccess(payload = {}) {
  return {
    type: FETCH_SUCCESS,
    payload,
  }
}

export function fetchFailure(errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(payload = {}) {
  return {
    type: UPDATE_SUCCESS,
    payload,
  }
}

export function updateFailure(errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(payload = {}) {
  return {
    type: DELETE_SUCCESS,
    payload,
  }
}

export function deleteFailure(errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadOrders(type, options) {
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api('/orders', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess({ data, type }))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function loadOrder(id, options) {
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/orders/${id}`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(data))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function loadUserOrders(userId, options) {
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/users/${userId}/context-express/orders`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function createOrder(order, options = {}) {
  const config = {
    method: 'POST',
    data: order,
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api(`/orders`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function createProjectOrder(order, options = {}) {
  const config = {
    method: 'POST',
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api(`/projects/${order.project}/orders/current`, order, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteOrder(order) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/orders/${order.id}`, { permanent: true }, config)
      .then(() => {
        const normalizedJson = normalize(order, Schemas.ORDER)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess({ type: order?.type || null }))

        return { success: true, result: order }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function loadOrderStripeLink(id, options) {
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/stripe/order/${id}`, options)
      .then(({ data }) => {
        dispatch(fetchSuccess(data))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function placeOrder(order, options = {}) {
  const config = {
    method: 'POST',
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/orders/${order.id}/place`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload, errors } = action

  switch (type) {
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
        type: payload?.type || null,
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
        type: payload?.type || null,
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
        type: payload?.type || null,
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors,
      }
    default:
      return state
  }
}
